/**
 * One resolved search context for the storefront, merging the D2C and B2B Algolia configurations.
 *
 * The B2B search app publishes `window.algoliaB2B` from a theme app extension: credentials, index names,
 * sort orders, facets, and the buyer's entitled catalog IDs. It is a CONFIG OVERLAY, not a replacement — it
 * ships no runtime. The theme reads nine members off `window.algoliaShopify` that have no B2B counterpart
 * (`externals.algoliasearch`, `externals.autocomplete`, `formatMoney`, `render`, `compileTemplate`, `assign`,
 * `translations`, `storeName`, `appendStyle`), so the official app stays installed and every caller keeps
 * taking its runtime from there. This module decides only WHICH credentials, indexes, sorts and facets apply.
 *
 * Resolution rules, each one load-bearing:
 *
 * - B2B applies only when the buyer is B2B AND entitled (`can_search`) AND credentials are published. A B2B
 *   buyer with no entitled catalog is `blocked`, never silently served retail results: an unfiltered query
 *   against the public B2B index returns the whole assortment at retail pricing, which is the exact failure
 *   the catalog filter exists to prevent.
 * - Presentational settings fall back to the D2C config. The B2B payload deliberately omits hits-per-page,
 *   `colors`, `analytics_enabled` and `collection_id_query_rules`.
 * - Articles and pages always resolve to the D2C indexes. Content is not catalog-scoped and the B2B app does
 *   not index it.
 * - Collection-scoped config is looked up by BOTH numeric id and GID. The theme's `data-collection-id` is
 *   `{{ collection.id }}` (numeric); the B2B app keys these maps by `gid://shopify/Collection/<id>`.
 * - A sort's index comes from the published `index_name` when there is one. Composing `<index><suffix>` is
 *   wrong on the B2B app: its "Newest" replica is suffixed `published_at_desc` while the record field is
 *   `published_at_timestamp`, so a composed name points at an index that does not exist.
 */
(function initializeAlgoliaContext(globalObject) {
  'use strict';

  var COLLECTION_GID_PREFIX = 'gid://shopify/Collection/';
  var CONTENT_INDEXES = ['articles', 'pages'];

  function collectionScoped(map, collectionId) {
    if (!map || !collectionId) return null;
    return map[collectionId] || map[COLLECTION_GID_PREFIX + collectionId] || null;
  }

  function nonEmptyArray(value) {
    return Array.isArray(value) && value.length > 0 ? value : null;
  }

  function resolveAlgoliaContext(retail, b2b) {
    var retailConfig = (retail && retail.config) || {};
    var b2bConfig = (b2b && b2b.config) || {};
    var entitlement = (b2b && b2b.b2b) || {};

    var isB2BBuyer = entitlement.is_b2b_customer === true;
    var canSearch = entitlement.can_search === true;
    var hasCredentials = Boolean(b2bConfig.app_id && b2bConfig.search_api_key);
    var active = isB2BBuyer && canSearch && hasCredentials;
    var source = active ? b2bConfig : retailConfig;

    function setting(key) {
      if (active && source[key] !== undefined && source[key] !== null) return source[key];
      return retailConfig[key];
    }

    function primaryIndexName(kind) {
      var indexKind = kind || 'products';
      if (active && CONTENT_INDEXES.indexOf(indexKind) === -1) {
        if (indexKind === 'products' && b2bConfig.products_index_name) return b2bConfig.products_index_name;
        if (indexKind === 'collections' && b2bConfig.collections_index_name) return b2bConfig.collections_index_name;
      }
      return (retailConfig.index_prefix || '') + indexKind;
    }

    // Three tiers, and the collection tiers apply only ON a collection page: this collection's override,
    // then the collection-page default, then the global list. Without a collection — the search results page
    // — the global list is the only correct source; letting the collection default win there resolved a
    // search sort from a list that does not describe search.
    function collectionScopedList(overrides, collectionDefault, global, collectionId) {
      if (collectionId) {
        return nonEmptyArray(collectionScoped(overrides, collectionId)) || nonEmptyArray(collectionDefault) || nonEmptyArray(global) || [];
      }
      return nonEmptyArray(global) || [];
    }

    function sortOrders(collectionId) {
      return collectionScopedList(source.collection_sort_orders, source.collection_default_sort_orders, source.sort_orders, collectionId);
    }

    function facets(collectionId) {
      return collectionScopedList(source.collection_facets, source.collection_default_facets, source.facets, collectionId);
    }

    // The index a sort selection resolves to. `sortValue` is the theme's own `_<key>_<direction>` form.
    function indexNameForSort(kind, sortValue, collectionId) {
      var base = primaryIndexName(kind);
      if (!sortValue) return base;

      var options = globalObject.ListingUtils ? globalObject.ListingUtils.sortOptionsFromConfig(sortOrders(collectionId)) : [];
      for (var index = 0; index < options.length; index += 1) {
        if (options[index].value === sortValue) {
          return options[index].indexName || base + sortValue;
        }
      }

      // An unrecognised selection keeps the legacy composition. The theme appends replicas of its own that
      // are not in any published config, such as the manually managed new-arrivals stock ordering.
      return base + sortValue;
    }

    function catalogFacetFilters() {
      if (!active) return null;
      if (b2b && typeof b2b.catalogFacetFilters === 'function') return b2b.catalogFacetFilters();

      var ids = entitlement.catalog_ids || [];
      if (!ids.length) return null;
      return [
        ids.map(function (id) {
          return 'catalogIds:' + id;
        }),
      ];
    }

    // Facet counts are computed before `distinct` collapses variant records, so a three-product result
    // reported vendor counts totalling 89. Set only for B2B: it requires distinct, which the D2C index
    // does not have.
    function queryDefaults() {
      return active ? { facetingAfterDistinct: true } : {};
    }

    return {
      active: active,
      isB2BBuyer: isB2BBuyer,
      blocked: Boolean(isB2BBuyer && (!canSearch || !hasCredentials)),
      appId: setting('app_id'),
      apiKey: setting('search_api_key'),
      indexPrefix: setting('index_prefix'),
      setting: setting,
      primaryIndexName: primaryIndexName,
      indexNameForSort: indexNameForSort,
      sortOrders: sortOrders,
      facets: facets,
      catalogFacetFilters: catalogFacetFilters,
      queryDefaults: queryDefaults,
    };
  }

  /**
   * The B2B payload, read from the DOM rather than from `window.algoliaB2B`.
   *
   * Both are published by the app embed, but the global is set by a DEFERRED script that Shopify renders at
   * the end of `body`, while this section's own deferred scripts run earlier in document order. Reading the
   * global therefore lost the race: the listing initialised with `algoliaB2B` undefined, resolved to the D2C
   * configuration, and queried the retail index for a B2B buyer — with no catalog filter. Measured on the dev
   * store before this fix: `active` was true when called later from the console, yet every query went to
   * `shopify_wll_stage_products`.
   *
   * The `<script type="application/json">` block is server-rendered, so it is in the DOM before any script
   * runs. Reading it makes resolution synchronous and race-free, and it also survives the embed's JavaScript
   * failing to load at all. `algolia-b2b:ready` remains available for anything that wants to react to the
   * global appearing; nothing here needs to wait for it.
   */
  function embeddedPayload() {
    var node = globalObject.document && globalObject.document.getElementById('algolia-b2b-config');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      // A malformed payload must not take the storefront's search with it. Falling through to the D2C
      // configuration is wrong for a B2B buyer, but `blocked` cannot be inferred from a parse failure.
      if (globalObject.console) globalObject.console.warn('[algolia-context] B2B configuration could not be parsed', error);
      return null;
    }
  }

  /**
   * Whether an out-of-stock product hit should be presented as "Coming soon" rather than "Sold out".
   *
   * Lives here rather than in ListingUtils because both product-card renderers load this module — the listing
   * and the header's search bar — while ListingUtils loads only on listing pages. Read from the variant
   * record's `meta.variant_meta.coming_soon`: the Algolia app indexes a metafield at `meta.<namespace>.<key>`,
   * and the product page reads this flag as `variant_meta.coming_soon`. The attribute lists previously requested
   * `meta.variant.coming_soon`, which does not match that namespace. An in-stock hit is never "coming soon",
   * matching the product page, which shows the coming-soon state only when the variant is unavailable. The
   * string form covers a metafield indexed as text rather than boolean.
   */
  function isHitComingSoon(hit, isInStock) {
    if (isInStock) return false;
    var flag = hit && hit.meta && hit.meta.variant_meta && hit.meta.variant_meta.coming_soon;
    return flag === true || flag === 'true';
  }

  globalObject.AlgoliaContext = {
    resolve: function resolve() {
      return resolveAlgoliaContext(globalObject.algoliaShopify, globalObject.algoliaB2B || embeddedPayload());
    },
    resolveFrom: resolveAlgoliaContext,
    embeddedPayload: embeddedPayload,
    isHitComingSoon: isHitComingSoon,
  };
})(globalThis);
